import { useMemo, type CSSProperties } from 'react';
import { eachDayOfInterval, endOfWeek, format, isSameDay, isToday, startOfWeek } from 'date-fns';
import { Link2 } from 'lucide-react';
import { cn } from '../utils/cn';
import type { CalendarEvent } from '../types/calendar';
import type { CalendarLinkSummary } from '../services/calendarLinkSources';
import { getEventSpans, layoutWeek, type EventSpan } from '../utils/calendarLayout';
import { describeLinks, describeSpan, EventBar } from './calendar/EventBar';
import { eventColor, tint, type TagColorLookup } from './calendar/eventColors';

interface TimelineViewProps {
  events: readonly CalendarEvent[];
  tagColors: TagColorLookup;
  links: ReadonlyMap<string, CalendarLinkSummary>;
  currentDate: Date;
  onEventClick: (event: CalendarEvent) => void;
}

interface TimedBar {
  span: EventSpan;
  dayIndex: number;
  /** Hours from midnight, 0–24. */
  startHour: number;
  endHour: number;
  /** Overlapping bars cascade to the right. */
  lane: number;
}

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const GRID_HEIGHT = 600;

/**
 * Week view. All-day and multi-day events live in a strip above the hours as
 * spanning bars, so the hour grid only carries events with a real time slot.
 */
export const TimelineView = ({ events, tagColors, links, currentDate, onEventClick }: TimelineViewProps) => {
  const weekKey = format(startOfWeek(currentDate, { weekStartsOn: 0 }), 'yyyy-MM-dd');
  const { weekStart, weekDays } = useMemo(() => {
    const start = startOfWeek(currentDate, { weekStartsOn: 0 });
    const end = endOfWeek(currentDate, { weekStartsOn: 0 });
    return { weekStart: start, weekDays: eachDayOfInterval({ start, end }) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekKey]);

  const { allDay, timedByDay } = useMemo(() => {
    const spans = getEventSpans(events);
    const blocks = spans.filter(span => span.shape === 'block');
    const timed = spans.filter(span => span.shape === 'timed');

    const byDay: TimedBar[][] = weekDays.map(() => []);
    for (const span of timed) {
      const dayIndex = weekDays.findIndex(day => isSameDay(day, span.firstDay));
      if (dayIndex === -1) continue;
      byDay[dayIndex].push({
        span,
        dayIndex,
        startHour: span.start.getHours() + span.start.getMinutes() / 60,
        endHour: span.end.getHours() + span.end.getMinutes() / 60,
        lane: 0,
      });
    }
    for (const bars of byDay) {
      bars.sort((a, b) => a.startHour - b.startHour || b.endHour - a.endHour);
      for (let i = 0; i < bars.length; i++) {
        const taken = new Set<number>();
        for (let j = 0; j < i; j++) {
          const other = bars[j];
          if (other.startHour < bars[i].endHour && other.endHour > bars[i].startHour) taken.add(other.lane);
        }
        let lane = 0;
        while (taken.has(lane)) lane++;
        bars[i].lane = lane;
      }
    }

    return { allDay: layoutWeek(blocks, weekStart), timedByDay: byDay };
  }, [events, weekDays, weekStart]);

  const now = new Date();
  const todayIndex = weekDays.findIndex(day => isToday(day));
  const nowOffset = ((now.getHours() + now.getMinutes() / 60) / 24) * 100;

  return (
    <div>
      {/* Day header */}
      <div className="grid grid-cols-8 border-b border-glass-200/10">
        <div />
        {weekDays.map(day => (
          <div key={day.toISOString()} className="py-2 text-center">
            <div className="eyebrow">{format(day, 'EEE')}</div>
            <div
              className={cn(
                'mx-auto mt-0.5 w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold tabular-nums',
                isToday(day) ? 'bg-accent-500 text-on-solid' : 'text-white',
              )}
            >
              {format(day, 'd')}
            </div>
          </div>
        ))}
      </div>

      {/* All-day strip */}
      <div className="grid grid-cols-8 border-b border-glass-200/10">
        <div className="eyebrow px-2 py-2 self-start">All day</div>
        <div
          className="col-span-7 grid grid-cols-7 py-1 min-h-[2.25rem]"
          style={allDay.laneCount > 0 ? { gridTemplateRows: `repeat(${allDay.laneCount}, 1.625rem)` } : undefined}
        >
          {allDay.segments.map(segment => (
            <EventBar
              key={`${segment.span.event.id}:${segment.startCol}`}
              span={segment.span}
              color={eventColor(segment.span.event, tagColors)}
              linked={links.get(segment.span.event.id)}
              continuesBefore={segment.continuesBefore}
              continuesAfter={segment.continuesAfter}
              onClick={onEventClick}
              className={cn('self-center', segment.continuesBefore ? 'ml-0' : 'ml-1', segment.continuesAfter ? 'mr-0' : 'mr-1')}
              style={{
                gridColumn: `${segment.startCol + 1} / span ${segment.endCol - segment.startCol + 1}`,
                gridRow: segment.lane + 1,
              }}
            />
          ))}
        </div>
      </div>

      {/* Hour grid */}
      <div className="relative grid grid-cols-8" style={{ height: GRID_HEIGHT }}>
        <div className="absolute inset-y-0 right-0 left-[12.5%] pointer-events-none">
          {HOURS.map(hour => (
            <div
              key={hour}
              className="absolute left-0 right-0 border-t border-glass-200/[0.08]"
              style={{ top: `${(hour / 24) * 100}%` }}
            />
          ))}
        </div>

        <div className="relative">
          {HOURS.map(hour => (
            <span
              key={hour}
              className="absolute right-2 -translate-y-1/2 text-[10px] tabular-nums text-glass-500"
              style={{ top: `${(hour / 24) * 100}%` }}
            >
              {hour > 0 ? `${String(hour).padStart(2, '0')}:00` : ''}
            </span>
          ))}
        </div>

        {weekDays.map((day, dayIndex) => (
          <div key={day.toISOString()} className={cn('relative border-l border-glass-200/10', isToday(day) && 'bg-accent-500/[0.04]')}>
            {timedByDay[dayIndex].map(bar => {
              const { event } = bar.span;
              const linked = links.get(event.id);
              const isLinked = !!linked && linked.targets.length > 0;
              const color = eventColor(event, tagColors);
              const top = (bar.startHour / 24) * 100;
              const height = Math.max(((bar.endHour - bar.startHour) / 24) * 100, 2);
              // Under ~40px there is only room for one line, so the time sits beside the title.
              const twoLines = (height / 100) * GRID_HEIGHT >= 40;
              const timeLabel = `${format(bar.span.start, 'HH:mm')} – ${format(bar.span.displayEnd, 'HH:mm')}`;
              const faded = event.status === 'cancelled' || event.status === 'postponed';
              return (
                <button
                  key={event.id}
                  type="button"
                  onClick={() => onEventClick(event)}
                  title={[event.title, describeSpan(bar.span), describeLinks(linked)].filter(Boolean).join(' · ')}
                  className={cn(
                    'absolute rounded-md text-left overflow-hidden px-1.5 py-1 transition-colors',
                    isLinked
                      ? 'border border-dashed hover:bg-glass-200/10'
                      : 'bg-[color:var(--bar-bg)] hover:bg-[color:var(--bar-bg-hover)]',
                    faded && 'opacity-60',
                  )}
                  style={
                    {
                      top: `${top}%`,
                      height: `${height}%`,
                      left: `${bar.lane * 8}%`,
                      right: 2,
                      zIndex: 10 + bar.lane,
                      '--bar-bg': isLinked ? undefined : tint(color, 0.22),
                      '--bar-bg-hover': isLinked ? undefined : tint(color, 0.34),
                      boxShadow: isLinked ? undefined : `inset 3px 0 0 ${color}`,
                      borderColor: isLinked ? tint(color, 0.55) : undefined,
                    } as CSSProperties
                  }
                >
                  <div className="flex items-center gap-1.5 min-w-0">
                    {isLinked && <Link2 className="w-3 h-3 shrink-0 text-glass-400" aria-hidden />}
                    <span
                      className={cn(
                        'truncate text-[11px] font-medium',
                        isLinked ? 'text-glass-400' : 'text-white',
                        event.status === 'cancelled' && 'line-through',
                        event.status === 'postponed' && 'italic',
                      )}
                    >
                      {event.title}
                    </span>
                    {!twoLines && <span className="ml-auto shrink-0 text-[10px] tabular-nums text-glass-400">{timeLabel}</span>}
                  </div>
                  {twoLines && <div className="text-[10px] tabular-nums text-glass-400">{timeLabel}</div>}
                </button>
              );
            })}

            {dayIndex === todayIndex && (
              <div className="absolute left-0 right-0 z-20 pointer-events-none" style={{ top: `${nowOffset}%` }} aria-hidden>
                <div className="h-px bg-red-400/80" />
                <div className="absolute -left-1 -top-[3px] w-[7px] h-[7px] rounded-full bg-red-400" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
